import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { API_PATCH, API_POST, DBRequest, useConfig } from '../../api/api';
import {
  Box,
  Button,
  ClickAwayListener,
  colors,
  Divider,
  IconButton,
  Link,
  Popover,
  Popper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { ECTopbar } from './ECTopbar';
import { tokens } from '../../global/theme/tokens';
import DateTimeLabel from '../label/DateTimeLabel';
import { ExoSkeleton } from '../exo/ExoSkeleton';
import ReplyIcon from '@mui/icons-material/Reply';
import ReplyAllIcon from '@mui/icons-material/ReplyAll';
import { useTranslation } from 'react-i18next';
import { ECSenderPreview } from './ECSenderPreview';
import CheckIcon from '@mui/icons-material/Check';
import { ECLinkSalesObject } from './ECLinkSalesObject';
import uuid from 'react-uuid';
import MediaPreview from '../exo/explorer/MediaPreview';
import MarqueeText from '../special/MarqueeText';
import SpeedDialMenu from '../menu/SpeedDialMenu';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import FlagIcon from '@mui/icons-material/Flag';
import EmailIcon from '@mui/icons-material/Email';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import DraftsIcon from '@mui/icons-material/Drafts';
import { hasPermissions } from '../../auth/hasPermissions';
import NoDataAnimation from '../animation/NoDataAnimation';
import { ECLinkProject } from './ECLinkProject';
import ExoDialog from '../exo/ExoDialog';
import NewReceipt from '../new/NewReceipt';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DOMPurify from 'dompurify';
import DateLabel from '../label/DateLabel';
import { TimeLabel } from '../label/TimeLabel';
import DurationLabel from '../label/DurationLabel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import moment from 'moment';
import VideocamIcon from '@mui/icons-material/Videocam';
import StyledTextContainer from '../preview/StyledTextContainer';
import RenderHTML from '../preview/RenderHTML';
import { NewMeeting } from '../new/NewMeeting';
import Feedback from '../special/Feedback';
import EventIcon from '@mui/icons-material/Event';
import ECFromStack from './ECFromStack';
import ECToStack from './ECToStack';
import { Inbox } from './ECInboxList';
import { NavLink } from 'react-router-dom';

export const ECEmailConversation = forwardRef(
  (
    {
      selectedEmail,
      inboxes,
      folderMap,
      selectedFolder,
      onEdit,
      onChange,
      onChangeView,
      isMobile,
      onDelete,
      onSelect,
    },
    ref
  ) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
    const config = useConfig();
    const { t } = useTranslation();

    const [lastId, setLastId] = useState(null);
    const [apiData, setApiData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const [fileToAccount, setFileToAccount] = useState(null);
    const [moveToFolderAnchorEl, setMoveToFolderAnchorEl] = useState(null);

    useEffect(() => {
      if (!selectedEmail || !selectedEmail?.id) return setApiData(null);
      if (selectedEmail && selectedEmail.id !== lastId) {
        setApiData(null);
        DBRequest({
          config,
          path: `imap-emails/${selectedEmail?.id}`,
          onResponse: handleResponse,
          onLoading: setIsLoading,
        });
      }
    }, [config, selectedEmail]);

    function handleAction(type, oldEmail) {
      onEdit({ type, oldEmail });
    }

    function handleResponse(res) {
      setApiData(res);

      const { isSeen, id } = res.data;
      setLastId(id);
      if (!isSeen)
        DBRequest({
          config,
          path: `imap-emails/${id}`,
          method: API_PATCH({ isSeen: true }),
          onResponse: (res) => {
            setApiData(res);
            onChange(res);
          },
        });
    }

    function updateEmail(email) {
      setApiData({ ...apiData, data: email });
    }

    // exposed functions
    useImperativeHandle(ref, () => ({
      updateEmail,
    }));

    function handleChange(res) {
      updateEmail(res.data);
      onChange(res);
    }

    const { id, isFlagged, salesObject, project, isSeen } = apiData?.data || {};

    const [anchorEl, setAnchorEl] = useState(null);

    function handleFlagClick() {
      DBRequest({
        config,
        path: `imap-emails/${id}`,
        method: API_PATCH({ isFlagged: !isFlagged }),
        onResponse: handleChange,
      });
    }

    function handleSeenClick() {
      DBRequest({
        config,
        path: `imap-emails/${id}`,
        method: API_PATCH({ isSeen: !isSeen }),
        onResponse: handleChange,
      });
    }

    function handleDeleteClick() {
      DBRequest({
        config,
        path: `imap-emails/bulk-delete`,
        method: API_POST({ emailIds: [apiData.data.id] }),
        onLoading: setIsLoading,
        onResponse: () => {
          setApiData(null);
          onDelete(apiData.data);
          setAnchorEl(null);
        },
      });
    }
    function handleSelect(folder) {
      setMoveToFolderAnchorEl(null);
      setAnchorEl(null);
      onSelect(folder);
      setLastId(null);
    }

    return (
      <Box
        className="flex flex-col w-full h-full rounded-lg overflow-hidden backdrop-blur-lg"
        sx={{
          backgroundColor: colors.glass,
        }}
      >
        <ECTopbar>
          {apiData && (
            <Box className="flex h-full items-center justify-between w-full">
              <Box className="flex gap-2">
                {hasPermissions('sales-object', 'read') && (
                  <ECLinkSalesObject
                    onChange={handleChange}
                    email={apiData.data}
                  />
                )}
                {hasPermissions('project', 'read') && (
                  <ECLinkProject onChange={handleChange} email={apiData.data} />
                )}
              </Box>

              <IconButton
                onClick={(e) =>
                  anchorEl ? null : setAnchorEl(e.currentTarget)
                }
              >
                <MoreVertIcon />
              </IconButton>

              <Popover
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                sx={{
                  '.MuiBackdrop-root': {
                    bgcolor: 'transparent',
                    background: 'unset',
                    backdropFilter: 'unset',
                  },
                  '.MuiPaper-root': {
                    bgcolor: 'transparent',
                    background: 'unset',
                    backdropFilter: 'unset',
                  },
                }}
                disable
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <Box
                  className="flex flex-col p-3 pt-2 rounded-lg gap-2"
                  sx={{ bgcolor: colors.glassSolid }}
                >
                  <Typography
                    fontSize="12px"
                    fontWeight={600}
                    className=" opacity-70"
                  >
                    {t('Actions')}
                  </Typography>
                  <Button
                    onClick={handleFlagClick}
                    variant="outlined"
                    color="warning"
                    size="small"
                    className="flex gap-2"
                    sx={{
                      justifyContent: 'start',
                    }}
                  >
                    {isFlagged ? (
                      <FlagIcon color="warning" />
                    ) : (
                      <OutlinedFlagIcon />
                    )}
                    {t('Mark')}
                  </Button>

                  <Button
                    onClick={handleSeenClick}
                    variant="outlined"
                    color="info"
                    size="small"
                    className="flex gap-2"
                    sx={{
                      justifyContent: 'start',
                    }}
                  >
                    {isSeen ? <DraftsIcon /> : <EmailIcon color="info" />}
                    {isSeen ? t('unread') : t('read')}
                  </Button>
                  <Button
                    onClick={(e) =>
                      moveToFolderAnchorEl
                        ? null
                        : setMoveToFolderAnchorEl(e.currentTarget)
                    }
                    variant="outlined"
                    color="info"
                    size="small"
                    className="flex gap-2"
                    sx={{
                      justifyContent: 'start',
                    }}
                  >
                    <DraftsIcon color="info" />
                    {t('move')}
                  </Button>

                  <Button
                    onClick={handleDeleteClick}
                    color="error"
                    variant="outlined"
                    size="small"
                    className="flex gap-2"
                    sx={{
                      justifyContent: 'start',
                    }}
                  >
                    <DeleteIcon color="error" />
                    {t('delete')}
                  </Button>
                </Box>
              </Popover>
            </Box>
          )}
          <Popover
            anchorEl={moveToFolderAnchorEl}
            open={Boolean(moveToFolderAnchorEl)}
            onClose={() => setMoveToFolderAnchorEl(null)}
            disable
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Box className="pt-2 pl-1 w-full md:max-w-[350px]">
              <Typography fontWeight={600} className="pl-2 pb-2">
                {t('Where should the email be moved?')}
              </Typography>
              {inboxes?.map((inbox) => (
                <Inbox
                  key={inbox.id}
                  inbox={inbox}
                  onSelect={handleSelect}
                  selectedFolder={selectedFolder}
                  folderMap={folderMap}
                  fromMove={true}
                />
              ))}
            </Box>
          </Popover>
        </ECTopbar>
        {isMobile && (
          <IconButton
            onClick={() => onChangeView('list')}
            sx={{
              position: 'absolute',
              bgcolor: colors.bgInput,
              height: '50px',
              width: '50px',
              borderRadius: '15px',
            }}
            className="bottom-2 left-2 z-10 backdrop-blur-md"
          >
            <ArrowBackIosNewIcon sx={{ fontSize: '30px' }} />
          </IconButton>
        )}
        <Box className="w-full h-full flex flex-col overflow-y-auto gap-4 p-2">
          {isLoading ? (
            <ExoSkeleton n={2} size="350px" />
          ) : apiData ? (
            <>
              <RenderEmail
                email={apiData.data}
                onAction={handleAction}
                onAccountFile={setFileToAccount}
                onFlagClick={handleFlagClick}
              />
              {apiData.data.previousEmails?.map((email) => (
                <RenderEmail key={email.id} email={email} />
              ))}
            </>
          ) : (
            <Box className="w-full flex items-center justify-center pt-[12px]">
              <NoDataAnimation size="150px" />
            </Box>
          )}
        </Box>
        <ExoDialog open={fileToAccount} onClose={() => setFileToAccount(false)}>
          <NewReceipt
            open={fileToAccount}
            autoStartFile={fileToAccount}
            onClose={() => setFileToAccount(false)}
          />
        </ExoDialog>
      </Box>
    );
  }
);

export const RenderEmail = ({
  email,
  onAction,
  onFlagClick,
  onAccountFile,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();

  const {
    date,
    htmlBody,
    textBody,
    to,
    cc,
    from,
    isSeen,
    isFlagged,
    id,
    attachments,
    subject,
    folder,
    icsData,
    senderEmailAddress,
  } = email;

  return (
    <Box sx={{ bgcolor: colors.card }} className="rounded-lg relative">
      <Box className="flex justify-between p-[3px] w-full overflow-hidden">
        <Box className="w-full overflow-hidden">
          <Typography fontWeight={600} className="pb-1 px-1">
            {subject}
          </Typography>

          {folder?.isOutgoing ? (
            <Box className="w-full flex flex-col pl-1">
              <ECToStack
                to={to}
                avatarSize="47px"
                cc={cc}
                showEmail
                openFromMobile={true}
              />
            </Box>
          ) : (
            <ECFromStack
              from={from}
              avatarSize="47px"
              to={to}
              cc={cc}
              showEmail
              openFromMobile={true}
            />
          )}
        </Box>

        <Box className="flex flex-col px-2">
          {onAction && (
            <Box className="flex">
              <IconButton size="small" onClick={() => onAction('reply', email)}>
                <ReplyIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onAction('replyAll', email)}
              >
                <ReplyAllIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onAction('forwarding', email)}
              >
                <ReplyIcon className=" -scale-x-100" />
              </IconButton>
            </Box>
          )}
          {date && (
            <Typography
              className="opacity-50 whitespace-nowrap"
              fontWeight={500}
              fontSize={11}
              textAlign="right"
            >
              <DateTimeLabel dateTime={date} useStrings />
            </Typography>
          )}
        </Box>
      </Box>
      {isFlagged && onFlagClick && (
        <Box>
          <Box
            sx={{ bgcolor: colors.warning + 30 }}
            className="flex gap-2 items-center px-1 py-[3px] justify-between"
          >
            <Box className="flex h-full items-center gap-2">
              <Box
                sx={{ bgcolor: colors.warning }}
                className="w-[5px] h-[15px] rounded-full"
              />
              <Typography sx={{ color: colors.warning }} fontSize={12}>
                {t('This message is marked for follow-up')}
              </Typography>
            </Box>
            <Button
              onClick={onFlagClick}
              variant="contained"
              color="success"
              disableElevation
              sx={{ padding: '0px 7px', fontSize: '11px' }}
            >
              {t('done')}
              <CheckIcon sx={{ fontSize: '14px' }} className="ml-[4px]" />
            </Button>
          </Box>
        </Box>
      )}

      <Box className="flex items-start w-full gap-2 flex-wrap px-4 py-1">
        {attachments?.map((att) => (
          <Attachment key={att.id} att={att} onAccountFile={onAccountFile} />
        ))}
      </Box>

      <Box className="flex flex-col w-full gap-3 px-2 pb-2">
        {icsData?.map((data) => (
          <RenderedIcsData
            icsData={data}
            from={from}
            senderEmailAddress={senderEmailAddress}
          />
        ))}
      </Box>

      <Divider className="opacity-50" />
      <Box
        sx={{
          padding: '6px',
          bgcolor: 'rgba(235, 235, 235, 0.8)',
        }}
      >
        <RenderHtmlEmail
          htmlBody={htmlBody || textBody.replace(/(?:\r\n|\r|\n)/g, '<br>')}
        />
      </Box>
    </Box>
  );
};

const RenderedIcsData = ({ icsData, from, senderEmailAddress }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();

  const [openParticipantsBlockTimes, setOpenParticipantsBlockTimes] =
    useState(false);
  const [openForm, setOpenForm] = useState(false);

  const {
    uid,
    label,
    startDateTime,
    endDateTime,
    location,
    agenda,
    onlineMeeting,
    wholeDay,
    meetingId,
  } = icsData;

  // Memoize startData to avoid recreating the object on every render
  const startData = useMemo(
    () => ({
      icalEventUid: uid,
      label,
      agenda,
      onlineMeeting,
      wholeDay,
      startDate: moment(startDateTime).format('YYYY-MM-DD'),
      endDate: moment(endDateTime).format('YYYY-MM-DD'),
      startTime: startDateTime,
      endTime: endDateTime,
      meetingIsConfirmed: true,
      blockTime: true,
      internalNote: t('Created from email invitation'),
      meetingPartner: from?.[0]?.participant || null,
      meetingPartnerType: from?.[0]?.participantType || null,
      participants: from?.[0]?.participantType
        ? [
            {
              ...from[0],
              email: from[0]?.email,
              type: from[0]?.participantType,
            },
          ]
        : [
            {
              type: 'custom',
              email: from[0]?.email,
            },
          ],
    }),
    [
      uid,
      label,
      agenda,
      onlineMeeting,
      wholeDay,
      startDateTime,
      endDateTime,
      from,
      t,
    ]
  );

  const integrationMap = {
    googleMeet: {
      logo: '/assets/meeting-provider/google-meet-logo.svg',
      height: '60%',
    },
    teams: {
      logo: '/assets/meeting-provider/teams-logo.png',
      height: '100%',
    },
    zoom: {
      logo: '/assets/meeting-provider/zoom-logo.svg',
      height: '50%',
    },
  };

  const daysDiff = moment(endDateTime).diff(moment(startDateTime), 'days');

  // meeting create & update logic
  function handleMeetingChange(dataCollection) {}

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });

  function handleFeedback(text, type) {
    setAlertState({
      alertOpen: true,
      alertText: text,
      alertType: type,
    });
  }

  return (
    <Box
      sx={{
        border: `1px solid ${colors.info}80`,
        bgcolor: colors.info + '20',
      }}
      className="rounded-md px-2 py-1 flex flex-col gap-2 relative"
    >
      <Box className="flex justify-between gap-2">
        <Box className="flex flex-col w-full">
          <Typography fontWeight={600} fontSize={12}>
            {label}
          </Typography>
          <Typography fontSize={11} className="opacity-50">
            {location}
          </Typography>
        </Box>
        {!meetingId && (
          <Tooltip title={t('Add to Calendar')}>
            <Button
              size="small"
              color="success"
              variant="outlined"
              onClick={() => setOpenForm(true)}
              sx={{ minWidth: 'unset', padding: '5px' }}
            >
              <EventIcon />
            </Button>
          </Tooltip>
        )}
      </Box>

      <Box className="flex gap-1 pt-2 flex-wrap">
        <Typography className="pr-4" fontWeight="500">
          <CalendarMonthIcon className="mr-2" />
          <DateLabel date={startDateTime} />
          {daysDiff > 0 && (
            <>
              {' - '}
              <DateLabel date={endDateTime} />
            </>
          )}
        </Typography>
        {!wholeDay && (
          <Typography className="pr-4" fontWeight="500">
            <AccessTimeIcon className="mr-2" />
            <TimeLabel time={startDateTime} format="HH:mm" />
            {` ${t("o'clock")}`}
          </Typography>
        )}
        {!wholeDay && (
          <Typography className="pr-4" fontWeight="500">
            <AvTimerIcon className="mr-2" />
            <DurationLabel
              startDateTime={startDateTime}
              endDateTime={endDateTime}
            />
          </Typography>
        )}
      </Box>
      {agenda && (
        <Box className="max-h-[200px] overflow-y-auto">
          <Typography fontWeight={500} fontSize={12}>
            {t('Agenda')}
          </Typography>
          <StyledTextContainer noWidth backgroundColor="transparent">
            <RenderHTML HTML={agenda} />
          </StyledTextContainer>
        </Box>
      )}
      {onlineMeeting && onlineMeeting.url && (
        <Box className="pb-1">
          <Typography fontWeight={500} fontSize={12} className="pb-1">
            {t('Online')}
          </Typography>
          <Box className="flex gap-2 items-center w-full h-[30px]">
            <Link href={onlineMeeting.url} target="_blank">
              <IconButton
                className="w-full"
                size="small"
                disableElevation
                sx={{
                  bgcolor: colors.info + '30',
                  border: `1px solid ${colors.info}80`,
                  ':hover': {
                    bgcolor: colors.info + '60',
                    border: `1px solid ${colors.info}80`,
                  },
                }}
              >
                <VideocamIcon className="px-[1px]" />
              </IconButton>
            </Link>
            {integrationMap[onlineMeeting.type] ? (
              <img
                src={integrationMap[onlineMeeting.type].logo}
                style={{
                  height: integrationMap[onlineMeeting.type].height,
                }}
                className="mr-2"
              />
            ) : (
              <Typography>{t('Online Meeting Link')}</Typography>
            )}
          </Box>
        </Box>
      )}

      <ExoDialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        limitWidth
        maxWidth={openParticipantsBlockTimes ? '1200px' : '800px'}
      >
        <NewMeeting
          startData={startData}
          onClose={() => {
            setOpenForm(null);
            setOpenParticipantsBlockTimes(false);
          }}
          onFeedback={handleFeedback}
          onCreate={handleMeetingChange}
          onUpdate={handleMeetingChange}
          openParticipantsBlockTimes={openParticipantsBlockTimes}
          setOpenParticipantsBlockTimes={setOpenParticipantsBlockTimes}
        />
      </ExoDialog>
      <Feedback setState={setAlertState} state={alertState} />
    </Box>
  );
};

export const RenderHtmlEmail = ({ htmlBody }) => {
  const cleanHtmlBody = DOMPurify.sanitize(htmlBody, {
    USE_PROFILES: { html: true }, // allow basic HTML but no scripts
    FORBID_TAGS: ['script'], // extra defense
    FORBID_ATTR: ['onerror', 'onclick', 'onload'],
  });

  return (
    <Box
      className="w-full"
      sx={{
        'body, root, span, p, b, strong, a, h1, h2, h3, h4, h5, h6, li, ol, ul, th, td, tr, div, i, em':
          {
            color: 'rgb(30,30,30)',
          },
      }}
    >
      <div
        dangerouslySetInnerHTML={{ __html: cleanHtmlBody }}
        className="w-full overflow-x-auto"
      />
    </Box>
  );
};

const formatEmailText = (text) => {
  // Replace email links: hamm@domain<mailto:hamm@domain>
  text = text.replace(
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})<mailto:\1>/g,
    '<a href="mailto:$1">$1</a>'
  );

  // Replace web links: das-innovations.de<https://...>
  text = text.replace(
    /([\w.-]+\.[a-z]{2,})<https?:\/\/[^>]+>/gi,
    (match, name) => {
      const urlMatch = match.match(/<([^>]+)>/);
      if (urlMatch) {
        return `<a href="${urlMatch[1]}" target="_blank" rel="noopener noreferrer">${name}</a>`;
      }
      return name;
    }
  );

  // Replace \r\n or \n with <br />
  text = text.replace(/(\r\n|\r|\n)/g, '<br />');

  return text;
};

const BodyTextEmailPreviewIframe = ({ rawText }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const fontUrl = `${config?.apiUrl}/fonts/`;

  const iframeRef = useRef(null);
  const [height, setHeight] = useState('200px');

  const iframeId = uuid(); // unique per instance

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.data?.type === 'setHeight' &&
        typeof event.data.height === 'number' &&
        event.data.id === iframeId // only update if it's *this* iframe
      ) {
        setHeight(event.data.height + 40 + 'px');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [iframeId]);

  const htmlBody = `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          body {
            font-family: montserrat;
            font-size: 14px;
            padding: 10px;
            margin: 0;
          }
          a {
            color: ${colors.info}!important;
            text-decoration: none;
          }
          a:hover {
            text-decoration: underline;
          }
          body, root, span, p, b, strong, a, h1, h2, h3, h4, h5, h6, li, ol, ul, th, td, tr, div {
            color: ${colors.text}!important;
            backgroundColor: transparent!important;
          }
          strong, strong>span, b, b>span{
            font-weight: 600!important;
          }
          *{
            margin: 0;
          }
        </style>
      </head>
      <body>
        ${formatEmailText(rawText)}
      <script>
          document.querySelectorAll('a').forEach(a => {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
          });
          function sendHeight() {
            window.parent.postMessage({ type: 'setHeight', height: document.body.scrollHeight, id: '${iframeId}' }, '*');
          }
          window.addEventListener('load', sendHeight);
          const observer = new ResizeObserver(sendHeight);
          observer.observe(document.body);
        </script>
      </body>
    </html>
  `;

  return (
    <iframe
      ref={iframeRef}
      title="Email Preview"
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
      srcDoc={htmlBody}
      style={{ width: '100%', height, border: 'none' }}
    />
  );
};

const BodyHtmlEmailPreviewIframe = ({ htmlBody, disableColorManipulation }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const iframeRef = useRef(null);
  const [height, setHeight] = useState('200px');
  const config = useConfig();
  const fontUrl = `${config?.apiUrl}/fonts/`;
  const iframeId = uuid(); // unique per instance

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.data?.type === 'setHeight' &&
        typeof event.data.height === 'number' &&
        event.data.id === iframeId // only update if it's *this* iframe
      ) {
        setHeight(event.data.height + 40 + 'px');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [iframeId]);

  const htmlWithEnhancements = `
    <!DOCTYPE html>
    <html>
      <head></head>
      <body>
      <style>
     ${
       !disableColorManipulation
         ? `a, a span, a p, a b, a strong, a h1, a h2, a h3, a h4, a h5, a h6, a li, a ol, a ul, a th, a td, a tr, a div {
            color: ${colors.info}!important;
            text-decoration: none;
            border-color: transparent!important;
          }
          a:hover {
            text-decoration: underline;
          }
          *, body, root, span, p, b, strong, a, h1, h2, h3, h4, h5, h6, li, ol, ul, th, td, tr, div, button {
            color: ${colors.text}!important;
          }
         *, body, root, span, p, b, strong, a, h1, h2, h3, h4, h5, h6, li, ol, ul, th, td, tr, div  {
            background-color: transparent!important;
            max-width: 100%;
          }
            button:{
            background-color: ${colors.info}!important;
            }
          
     `
         : ''
     } 
          body {
            font-family: montserrat;
            font-size: 14px;
            padding: 10px;
            margin: 0;
            font-weight: 400;
            border-radius: 10px;
          }
          strong, strong>span, b, b>span{
            font-weight: 500!important;
          }
          @font-face {
            font-family: 'Montserrat';
            src: url('${fontUrl}Montserrat.ttf') format('truetype');
            font-weight: 100 900;
            font-stretch: 75% 100%;
            font-style: normal;
          }
          @font-face {
            font-family: 'Montserrat';
            src: url('${fontUrl}Montserrat-Italic.ttf')
              format('truetype');
            font-weight: 100 900;
            font-stretch: 75% 100%;
            font-style: italic;
          }
          *{
            margin: 0;
          }    
     </style>
        ${htmlBody}
        <script>
          document.querySelectorAll('a').forEach(a => {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
          });
          function sendHeight() {
            window.parent.postMessage({ type: 'setHeight', height: document.body.scrollHeight, id: '${iframeId}' }, '*');
          }
          window.addEventListener('load', sendHeight);
          const observer = new ResizeObserver(sendHeight);
          observer.observe(document.body);
        </script>
      </body>
    </html>
  `;

  return (
    <iframe
      ref={iframeRef}
      title="Email Preview"
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
      srcDoc={htmlWithEnhancements}
      style={{ width: '100%', height, border: 'none', overflow: 'hidden' }}
    />
  );
};

export const Attachment = ({ att, onAccountFile }) => {
  const { t } = useTranslation();
  const actions = [
    /*{
      icon: <DownloadIcon />,
      name: t('Add Receipt'),
      onClick: () => {
        onAccountFile(att);
      },
    },*/
    {
      icon: <DownloadIcon />,
      name: t('Download'),
      onClick: () => {
        window.open(att.thumbnailUrl || att.temporaryUrl, '_blank');
      },
    },
  ];
  return (
    <Box className="flex gap-1 items-center">
      <MediaPreview
        src={att.thumbnailUrl || att.temporaryUrl}
        size="30px"
        mimeType={att.mimeType}
        allowPreview
      />
      <MarqueeText text={att.title} width="200px" />
      <SpeedDialMenu actions={actions} className=" z-10" noBlur />
    </Box>
  );
};
